import { useState } from 'react';
import { MapPin, Clock, Users, Wifi, Coffee, Car, X, ChevronLeft, ChevronRight, Navigation, Map, Satellite } from 'lucide-react';

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';

// Girne Koleji Campus Images
const venueImages = [
    {
        id: 1,
        src: 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/9dowjn1z_2025-05-07.webp',
        alt: 'Girne Koleji Kampüs Havadan Görünüm',
        captionKey: 'Girne Koleji Kampüs'
    },
    {
        id: 2,
        src: 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/zyjlsukm_WhatsApp%20Image%202026-01-26%20at%2012.58.35.jpeg',
        alt: 'Girne Koleji Ana Giriş',
        captionKey: 'Ana Giriş'
    },
    {
        id: 3,
        src: 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/tmljxdos_WhatsApp%20Image%202026-01-26%20at%2012.48.43.jpeg',
        alt: 'Girne Koleji Bina',
        captionKey: 'Ana Bina'
    },
    {
        id: 4,
        src: 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/4sxfw3fa_WhatsApp%20Image%202026-01-26%20at%2012.58.52%20%281%29.jpeg',
        alt: 'Girne Koleji Giriş',
        captionKey: 'Kampüs Girişi'
    },
    {
        id: 5,
        src: 'https://customer-assets.emergentagent.com/job_28206353-b4b4-425f-a884-c6ee1dcb5434/artifacts/93vu32a7_WhatsApp%20Image%202026-01-26%20at%2012.48.44.jpeg',
        alt: 'Girne Koleji Yön Tabelası',
        captionKey: 'Konferans Alanları'
    }
];

const VenuePage = () => {
    const { t, language } = useLanguage();
    const [selectedImage, setSelectedImage] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [mapView, setMapView] = useState('roadmap'); // 'roadmap' or 'satellite'

    // Google Maps URLs
    const VENUE_LOCATION = "Girne+Koleji,+Adana,+Turkey";
    const MAP_EMBED_BASE = "https://www.google.com/maps/embed/v1/place";
    const MAP_API_KEY = "AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8"; // Free embed API key
    
    const getMapUrl = (type) => {
        return `${MAP_EMBED_BASE}?key=${MAP_API_KEY}&q=${VENUE_LOCATION}&maptype=${type}&zoom=15`;
    };

    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${VENUE_LOCATION}`;

    const amenities = [
        { icon: Wifi, label: t('venue.amenities.wifi'), description: t('venue.amenities.wifiDesc') },
        { icon: Coffee, label: t('venue.amenities.refreshments'), description: t('venue.amenities.refreshmentsDesc') },
        { icon: Car, label: t('venue.amenities.parking'), description: t('venue.amenities.parkingDesc') },
        { icon: Users, label: t('venue.amenities.capacity'), description: t('venue.amenities.capacityDesc') }
    ];

    const schedule = [
        { day: t('venue.schedule.day1'), events: [t('venue.schedule.openingCeremony'), t('venue.schedule.committeeSession1'), t('venue.schedule.welcomeReception')] },
        { day: t('venue.schedule.day2'), events: [t('venue.schedule.committeeSession2'), t('venue.schedule.committeeSession3'), t('venue.schedule.socialEvent')] },
        { day: t('venue.schedule.day3'), events: [t('venue.schedule.committeeSession4'), t('venue.schedule.closingCeremony'), t('venue.schedule.awardsDistribution')] }
    ];

    const openGallery = (index) => {
        setCurrentImageIndex(index);
        setSelectedImage(venueImages[index]);
    };

    const closeGallery = () => {
        setSelectedImage(null);
    };

    const navigateGallery = (direction) => {
        let newIndex;
        if (direction === 'prev') {
            newIndex = currentImageIndex === 0 ? venueImages.length - 1 : currentImageIndex - 1;
        } else {
            newIndex = currentImageIndex === venueImages.length - 1 ? 0 : currentImageIndex + 1;
        }
        setCurrentImageIndex(newIndex);
        setSelectedImage(venueImages[newIndex]);
    };

    return (
        <>
            {/* Hero Section with Main Venue Image */}
            <section className="relative pt-18 h-[70vh] min-h-[500px]">
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url('${venueImages[0].src}')`
                    }}
                />
                <div className="hero-overlay absolute inset-0" />
                
                <div className="relative z-10 h-full flex items-end">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 w-full">
                        <p className="text-accent font-semibold text-sm uppercase tracking-wider mb-4">
                            {t('venue.subtitle')}
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground mb-4">
                            {t('venue.title')}
                        </h1>
                        <div className="flex items-center gap-2 text-primary-foreground/80">
                            <MapPin className="w-5 h-5 text-accent" />
                            <span className="text-lg">Girne Koleji Kampüsü, Adana</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Venue Details */}
            <section className="py-20 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                        {/* Left Column - Info */}
                        <div>
                            <h2 className="font-display text-3xl font-bold text-foreground mb-6 gold-underline gold-underline-left">
                                {t('venue.aboutVenue')}
                            </h2>
                            <div className="mt-10 space-y-6 text-muted-foreground leading-relaxed">
                                <p>{t('venue.aboutVenueDesc1')}</p>
                                <p>{t('venue.aboutVenueDesc2')}</p>
                            </div>

                            {/* Amenities */}
                            <div className="mt-10">
                                <h3 className="font-display text-xl font-semibold text-foreground mb-6">
                                    {t('venue.venueAmenities')}
                                </h3>
                                <div className="grid grid-cols-2 gap-4">
                                    {amenities.map((amenity, index) => (
                                        <div 
                                            key={index}
                                            className="p-4 rounded-lg bg-card border border-border"
                                        >
                                            <amenity.icon className="w-6 h-6 text-accent mb-2" />
                                            <h4 className="font-semibold text-foreground text-sm">
                                                {amenity.label}
                                            </h4>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {amenity.description}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Image Gallery */}
                        <div>
                            {/* Main Large Image */}
                            <button
                                onClick={() => openGallery(0)}
                                className="relative w-full rounded-xl overflow-hidden group cursor-pointer mb-4 bg-muted"
                            >
                                <img
                                    src={venueImages[0].src}
                                    alt={venueImages[0].alt}
                                    className="w-full h-auto transition-transform duration-300 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/40 transition-colors flex items-center justify-center">
                                    <span className="text-primary-foreground font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                                        {language === 'tr' ? 'Galeriyi Aç' : 'View Gallery'}
                                    </span>
                                </div>
                            </button>

                            {/* 3 Smaller Images Below */}
                            <div className="grid grid-cols-3 gap-4">
                                {venueImages.slice(1, 4).map((image, index) => (
                                    <button
                                        key={image.id}
                                        onClick={() => openGallery(index + 1)}
                                        className="relative rounded-xl overflow-hidden group cursor-pointer bg-muted"
                                    >
                                        <img
                                            src={image.src}
                                            alt={image.alt}
                                            className="w-full h-auto transition-transform duration-300 group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/40 transition-colors" />
                                    </button>
                                ))}
                            </div>

                            <p className="text-center text-sm text-muted-foreground mt-4">
                                {t('venue.clickToView')}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Schedule Overview */}
            <section className="py-20 section-cream">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <p className="text-accent font-semibold text-sm uppercase tracking-wider mb-3">
                            {t('venue.conferenceSchedule')}
                        </p>
                        <h2 className="font-display text-4xl font-bold text-foreground gold-underline mb-8">
                            {t('venue.threeDays')}
                        </h2>
                        
                        {/* Stay Tuned Card */}
                        <div className="max-w-md mx-auto p-10 rounded-xl bg-primary border-2 border-accent/30 shadow-elegant-lg">
                            <p className="font-display text-3xl sm:text-4xl font-bold text-accent">
                                Stay Tuned
                            </p>
                            <p className="text-primary-foreground/70 mt-3">
                                {language === 'tr' ? 'Program yakında açıklanacak' : 'Schedule coming soon'}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Location Map Placeholder */}
            <section className="py-20 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="font-display text-3xl font-bold text-foreground mb-6 gold-underline gold-underline-left">
                                {t('venue.gettingThere')}
                            </h2>
                            <div className="mt-10 space-y-4 text-muted-foreground">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                                        <MapPin className="w-5 h-5 text-accent" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-foreground">{t('venue.address')}</h4>
                                        <p>Girne Koleji Kampüsü, Adana, Turkey</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                                        <Car className="w-5 h-5 text-accent" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-foreground">{t('venue.byCar')}</h4>
                                        <p>{t('venue.freeParking')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Interactive Map */}
                        <div className="space-y-4">
                            {/* Map View Toggle Buttons */}
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setMapView('roadmap')}
                                    className={cn(
                                        "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                                        mapView === 'roadmap'
                                            ? "bg-primary text-primary-foreground shadow-md"
                                            : "bg-card border border-border text-muted-foreground hover:bg-muted hover:text-foreground"
                                    )}
                                >
                                    <Map className="w-4 h-4" />
                                    {language === 'tr' ? 'Harita Görünümü' : 'Map View'}
                                </button>
                                <button
                                    onClick={() => setMapView('satellite')}
                                    className={cn(
                                        "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                                        mapView === 'satellite'
                                            ? "bg-primary text-primary-foreground shadow-md"
                                            : "bg-card border border-border text-muted-foreground hover:bg-muted hover:text-foreground"
                                    )}
                                >
                                    <Satellite className="w-4 h-4" />
                                    {language === 'tr' ? 'Uydu Görünümü' : 'Satellite View'}
                                </button>
                            </div>

                            {/* Google Maps Embed */}
                            <div className="relative rounded-xl overflow-hidden shadow-lg border border-border">
                                <iframe
                                    title="Girne Koleji Location"
                                    src={getMapUrl(mapView)}
                                    className="w-full h-[320px] md:h-[420px]"
                                    style={{ border: 0 }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>

                            {/* Directions Button */}
                            <Button
                                onClick={() => window.open(directionsUrl, '_blank')}
                                className="w-full sm:w-auto btn-premium"
                            >
                                <Navigation className="w-4 h-4 mr-2" />
                                <span>{language === 'tr' ? 'Yol Tarifi Al' : 'Get Directions'}</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Image Gallery Modal */}
            <Dialog open={!!selectedImage} onOpenChange={closeGallery}>
                <DialogContent className="max-w-4xl p-0 bg-primary border-none">
                    <DialogTitle className="sr-only">
                        Image Gallery
                    </DialogTitle>
                    <div className="relative">
                        <button
                            onClick={closeGallery}
                            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <button
                            onClick={() => navigateGallery('prev')}
                            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 transition-colors"
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>

                        <button
                            onClick={() => navigateGallery('next')}
                            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 transition-colors"
                        >
                            <ChevronRight className="w-6 h-6" />
                        </button>

                        {selectedImage && (
                            <>
                                <img
                                    src={selectedImage.src}
                                    alt={selectedImage.alt}
                                    className="w-full max-h-[80vh] object-contain"
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-primary to-transparent">
                                    <p className="text-primary-foreground font-display text-xl font-semibold">
                                        {selectedImage.captionKey}
                                    </p>
                                    <p className="text-primary-foreground/70 text-sm mt-1">
                                        {currentImageIndex + 1} / {venueImages.length}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default VenuePage;
